import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { 
  Briefcase, 
  Layers, 
  Compass, 
  ArrowRight, 
  Mail, 
  Phone, 
  CheckCircle,
  Menu,
  X
} from "lucide-react";
import { TermsOfUsePage, PrivacyPolicyPage, SmsTermsPage } from "./LegalPages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// TDCR checkout Stripe setup (test mode sandbox — merchant identity is TDCR Consulting, LLC only)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
  developerTools: { assistant: { enabled: false } },
});
const TDCR_STRIPE_APPEARANCE = {
  theme: "stripe",
  variables: {
    colorPrimary: "#e41c22",
    colorBackground: "#ffffff",
    colorText: "#111111",
    colorDanger: "#e41c22",
    fontFamily: "Inter, sans-serif",
    fontSizeBase: "16px",
    borderRadius: "6px",
    spacingUnit: "4px",
  },
  rules: {
    ".Input": {
      border: "2px solid #e9e3d5",
      padding: "10px 12px",
      boxShadow: "none",
    },
    ".Input:focus": {
      border: "2px solid #e41c22",
      boxShadow: "none",
    },
    ".Label": {
      fontWeight: "700",
      fontSize: "14px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#111111",
    },
  },
};

// Captures the stripe/elements instances from inside the Elements provider tree
// so the outer form's submit handler (which owns validation + button state) can use them.
function TDCRStripeCapture({ onReady }) {
  const stripe = useStripe();
  const elements = useElements();
  useEffect(() => {
    onReady(stripe, elements);
  }, [stripe, elements, onReady]);
  return null;
}

// Custom Scroll to Top on route change
function ScrollToTop() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [navigate]);
  return null;
}

// Global Header Component
export function Header({ isBackToHome = false }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-gray-100 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Text only */}
          <Link 
            to="/" 
            className="text-lg font-bold tracking-[0.15em] text-[#111827] hover:text-[#800020] transition-colors duration-200 uppercase font-serif-editorial"
            data-testid="header-logo-link"
          >
            TDCR CONSULTING
          </Link>

          {/* Navigation Links */}
          {isBackToHome ? (
            <Link
              to="/"
              className="text-sm font-medium tracking-widest text-[#4B5563] hover:text-[#800020] transition-colors duration-200 flex items-center gap-2 uppercase"
              data-testid="header-back-home-link"
            >
              ← Back to Home
            </Link>
          ) : (
            <>
              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center gap-10">
                <Link
                  to="/"
                  className="text-sm font-semibold tracking-widest text-[#111827] hover:text-[#800020] transition-colors duration-200 uppercase"
                  data-testid="header-home-link"
                >
                  Home
                </Link>
                <Link
                  to="/contact"
                  className="px-5 py-2.5 bg-[#800020] hover:bg-[#660019] text-white text-xs font-semibold tracking-widest uppercase rounded-md shadow-sm transition-colors duration-200"
                  data-testid="header-contact-link"
                >
                  Contact
                </Link>
              </nav>

              {/* Mobile menu trigger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-[#800020] transition-colors duration-200"
                aria-label="Toggle menu"
                data-testid="header-mobile-menu-trigger"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && !isBackToHome && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-b border-gray-100 bg-white"
          >
            <div className="px-4 pt-4 pb-6 space-y-4 flex flex-col">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-semibold tracking-widest text-[#111827] hover:text-[#800020] py-2 border-b border-gray-50 uppercase"
                data-testid="header-mobile-home-link"
              >
                Home
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-block text-center px-5 py-3 bg-[#800020] hover:bg-[#660019] text-white text-xs font-semibold tracking-widest uppercase rounded-md shadow-sm transition-colors duration-200"
                data-testid="header-mobile-contact-link"
              >
                Contact
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// Global Footer Component
export function Footer() {
  return (
    <footer className="bg-[#111827] text-gray-300 py-8 sm:py-12 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 border-b border-gray-800 pb-6">
          <div className="text-xl font-bold tracking-[0.15em] text-white font-serif-editorial">
            TDCR CONSULTING
          </div>
          
          {/* Footer Navigation */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs font-semibold tracking-widest uppercase">
            <a 
              href="https://tdcrconsulting.com/" 
              className="hover:text-[#800020] transition-colors duration-200"
              data-testid="footer-home-link"
            >
              Home
            </a>
            <span className="text-gray-600">•</span>
            <Link 
              to="/contact" 
              className="hover:text-[#800020] transition-colors duration-200"
              data-testid="footer-contact-link"
            >
              Contact Us
            </Link>
            <span className="text-gray-600">•</span>
            <Link 
              to="/terms" 
              className="hover:text-[#800020] transition-colors duration-200"
              data-testid="footer-terms-link"
            >
              Terms
            </Link>
            <span className="text-gray-600">•</span>
            <Link 
              to="/privacy" 
              className="hover:text-[#800020] transition-colors duration-200"
              data-testid="footer-privacy-link"
            >
              Privacy
            </Link>
            <span className="text-gray-600">•</span>
            <Link 
              to="/sms-terms" 
              className="hover:text-[#800020] transition-colors duration-200"
              data-testid="footer-sms-terms-link"
            >
              SMS Terms
            </Link>
          </div>
        </div>
        
        <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <p data-testid="footer-copyright-text">
            © 2026 TDCR Consulting, LLC. All Rights Reserved.
          </p>
          <p className="font-light">
            Turning complexity into actionable resources.
          </p>
        </div>
      </div>
    </footer>
  );
}

// Homepage Component
function HomePage() {
  useEffect(() => {
    document.title = "TDCR Consulting | Digital Products & Consulting Services";
  }, []);

  // Standard animation config for reveals
  const fader = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const containerFader = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111827]">
      <Header />

      {/* HERO SECTION */}
      <section className="relative overflow-hidden pt-6 sm:pt-10 pb-10 sm:pb-14 border-b border-gray-100 bg-gradient-to-b from-white to-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Hero text */}
            <motion.div 
              className="lg:col-span-7 space-y-4"
              initial="hidden"
              animate="visible"
              variants={containerFader}
            >
              <motion.span 
                className="inline-block text-xs font-bold tracking-[0.25em] text-[#800020] uppercase bg-[#800020]/5 px-3 py-1 rounded-full"
                variants={fader}
              >
                DIGITAL PRODUCTS • CONSULTING • BUSINESS SOLUTIONS
              </motion.span>
              
              <motion.h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.1] font-serif-editorial"
                variants={fader}
                data-testid="hero-title"
              >
                PRACTICAL SOLUTIONS. DIGITAL PRODUCTS. SPECIALIZED CONSULTING.
              </motion.h1>
              
              <motion.div className="space-y-4 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl" variants={fader}>
                <p>
                  TDCR Consulting develops educational products, digital resources, document-based tools, and specialized consulting solutions for consumers and businesses.
                </p>
                <p>
                  We operate and support a growing portfolio of niche websites and digital products designed to turn complicated information, processes, and operational challenges into practical resources people can actually use.
                </p>
              </motion.div>
              
              <motion.div className="pt-2" variants={fader}>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-[#800020] hover:bg-[#660019] text-white text-sm font-semibold tracking-widest uppercase rounded-md shadow-sm transition-all duration-300 hover:shadow-md transform hover:-translate-y-0.5"
                  data-testid="hero-contact-button"
                >
                  Contact Us
                  <ArrowRight size={16} />
                </Link>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div 
              className="lg:col-span-5 relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <div className="relative aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5] rounded-lg overflow-hidden shadow-lg border border-gray-100 group">
                <img
                  src="https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1vZGVybiUyMHdvcmtzcGFjZSUyMGRlc2t8ZW58MHx8fHwxNzg2OTk5ODM5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern business workspace with laptop and documents"
                  className="object-cover w-full h-full transform transition-transform duration-700 group-hover:scale-105"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-60 pointer-events-none" />
                
                {/* Accent border highlight */}
                <div className="absolute inset-0 border-2 border-[#800020]/10 rounded-lg pointer-events-none" />
              </div>
              
              {/* Abstract decorative graphic mimicking high-end layout */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#800020]/5 rounded-full blur-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: WHAT WE DO */}
      <section className="py-10 sm:py-14 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center md:text-left mb-8 space-y-3">
            <span className="text-xs font-bold tracking-[0.25em] text-[#800020] uppercase bg-[#800020]/5 px-3 py-1 rounded-full">
              OUR CAPABILITIES
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 font-serif-editorial">
              WHAT WE DO
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {/* DIGITAL PRODUCTS */}
            <motion.div 
              className="bg-white p-6 lg:p-8 border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              data-testid="what-we-do-digital-products"
            >
              <div className="w-12 h-12 bg-[#800020]/5 rounded-md flex items-center justify-center text-[#800020] mb-4">
                <Layers size={24} />
              </div>
              <span className="text-xs font-bold tracking-widest text-[#800020] uppercase font-mono block mb-2">
                01 / PRODUCTS
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 font-serif-editorial">
                DIGITAL PRODUCTS
              </h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                We develop practical educational products, guides, document resources, templates, and digital tools designed around specific consumer and business needs.
              </p>
            </motion.div>

            {/* CONSULTING SERVICES */}
            <motion.div 
              className="bg-white p-6 lg:p-8 border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 animate-delay-1"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.15 }}
              data-testid="what-we-do-consulting-services"
            >
              <div className="w-12 h-12 bg-[#800020]/5 rounded-md flex items-center justify-center text-[#800020] mb-4">
                <Briefcase size={24} />
              </div>
              <span className="text-xs font-bold tracking-widest text-[#800020] uppercase font-mono block mb-2">
                02 / SUPPORT
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 font-serif-editorial">
                CONSULTING SERVICES
              </h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                We provide specialized consulting and strategic support for projects that benefit from focused research, digital systems, operational planning, and customized solutions.
              </p>
            </motion.div>

            {/* NICHE DIGITAL BRANDS */}
            <motion.div 
              className="bg-white p-6 lg:p-8 border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 animate-delay-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.3 }}
              data-testid="what-we-do-niche-brands"
            >
              <div className="w-12 h-12 bg-[#800020]/5 rounded-md flex items-center justify-center text-[#800020] mb-4">
                <Compass size={24} />
              </div>
              <span className="text-xs font-bold tracking-widest text-[#800020] uppercase font-mono block mb-2">
                03 / OPERATE
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 font-serif-editorial">
                NICHE DIGITAL BRANDS
              </h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                TDCR Consulting develops and supports specialized websites and digital properties focused on clearly defined markets, products, and customer needs.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 3: TURNING COMPLEXITY */}
      <section className="py-10 sm:py-14 bg-[#FAFAFA] border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Image (What We Do Image) */}
            <motion.div 
              className="lg:col-span-5 relative order-last lg:order-first"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-lg border border-gray-100 group">
                <img
                  src="https://images.pexels.com/photos/8428063/pexels-photo-8428063.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Professional working on computer with charts and documents"
                  className="object-cover w-full h-full transform transition-transform duration-700 group-hover:scale-103"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
              </div>
            </motion.div>

            {/* Right Copy */}
            <motion.div 
              className="lg:col-span-7 space-y-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-xs font-bold tracking-[0.25em] text-[#800020] uppercase bg-[#800020]/5 px-3 py-1 rounded-full inline-block">
                OUR METHODOLOGY
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 font-serif-editorial leading-tight">
                TURNING COMPLEXITY INTO PRACTICAL SOLUTIONS
              </h2>
              <div className="space-y-4 text-gray-600 text-base sm:text-lg leading-relaxed font-light" data-testid="complexity-text">
                <p>
                  Our operational approach connects specialized consulting with scalable digital resources. We identify areas where consumers or businesses face unnecessary complexity, then develop focused products, tools, information, and systems designed to make those challenges easier to understand and act on.
                </p>
                <p>
                  Rather than trying to serve every market through a single brand, TDCR Consulting develops targeted solutions through specialized websites and products built around specific needs.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4: MISSION & MANIFESTO */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Copy */}
            <motion.div 
              className="lg:col-span-7 space-y-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-xs font-bold tracking-[0.25em] text-[#800020] uppercase bg-[#800020]/5 px-3 py-1 rounded-full inline-block">
                PHILOSOPHY
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 font-serif-editorial" data-testid="mission-title">
                OUR MISSION
              </h2>
              
              <div className="space-y-5 text-gray-600 text-base sm:text-lg leading-relaxed font-light">
                <div className="flex gap-4 items-start">
                  <div className="text-[#800020] font-mono text-sm pt-1 font-bold">01 /</div>
                  <p>
                    Our mission is simple: create useful products and practical solutions that make complicated information easier to understand, organize, and use.
                  </p>
                </div>
                
                <div className="flex gap-4 items-start">
                  <div className="text-[#800020] font-mono text-sm pt-1 font-bold">02 /</div>
                  <p>
                    We believe good educational resources should be clear, actionable, accessible, and focused on helping customers make more informed decisions.
                  </p>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="text-[#800020] font-mono text-sm pt-1 font-bold">03 /</div>
                  <p>
                    TDCR Consulting, LLC combines research, digital development, practical experience, and focused consulting to build resources that provide real-world value.
                  </p>
                </div>
              </div>
              
              <div className="pt-1">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest text-[#800020] hover:text-[#660019] uppercase group transition-colors duration-200"
                  data-testid="mission-contact-link"
                >
                  Work With Us
                  <ArrowRight size={16} className="transform transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div 
              className="lg:col-span-5 relative"
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5] rounded-lg overflow-hidden shadow-lg border border-gray-100 group">
                <img
                  src="https://images.unsplash.com/photo-1558478551-1a378f63328e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMG1vZGVybiUyMHdvcmtzcGFjZSUyMGRlc2t8ZW58MHx8fHwxNzg2OTk5ODM5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern workspace desk with laptop and notebook"
                  className="object-cover w-full h-full transform transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL INTERACTIVE ACCENT ELEMENT: EDITORIAL MARQUEE */}
      <section className="py-12 bg-[#800020] text-white/90 overflow-hidden font-serif-editorial">
        <div className="whitespace-nowrap flex gap-12 text-2xl tracking-[0.15em] uppercase font-light opacity-95">
          <div className="animate-marquee flex gap-12 shrink-0">
            <span>• PRACTICAL SOLUTIONS</span>
            <span>• DIGITAL PRODUCTS</span>
            <span>• SPECIALIZED CONSULTING</span>
            <span>• TDCR CONSULTING, LLC</span>
            <span>• DETAILED RESEARCH</span>
          </div>
          <div className="animate-marquee flex gap-12 shrink-0" aria-hidden="true">
            <span>• PRACTICAL SOLUTIONS</span>
            <span>• DIGITAL PRODUCTS</span>
            <span>• SPECIALIZED CONSULTING</span>
            <span>• TDCR CONSULTING, LLC</span>
            <span>• DETAILED RESEARCH</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Contact Page Component
function ContactPage() {
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = "Contact TDCR Consulting";
    // Add canonical meta dynamically
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://tdcrconsulting.com/contact");
    }
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const tempErrors = {};
    if (!form.name.trim() || form.name.trim().length < 2) {
      tempErrors.name = "Name is required and must be at least 2 characters.";
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) {
      tempErrors.email = "Email is required.";
    } else if (!emailRegex.test(form.email)) {
      tempErrors.email = "Please enter a valid email address.";
    }
    
    if (!form.message.trim() || form.message.trim().length < 2) {
      tempErrors.message = "Message is required and must be at least 2 characters.";
    }
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error dynamically as user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form)
      });
      
      const data = await response.json();
      if (response.ok && data.ok) {
        toast.success("Thank you! Message submitted successfully.");
        // Redirect to thank-you route
        navigate("/thank-you");
      } else {
        toast.error(data.detail || "Something went wrong. Please try again later.");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Unable to connect to server. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111827]">
      <Header isBackToHome={true} />

      <section className="pt-6 sm:pt-10 pb-10 sm:pb-14 max-w-4xl mx-auto px-4">
        <div className="text-center space-y-3 mb-8">
          <span className="text-xs font-bold tracking-[0.25em] text-[#800020] uppercase bg-[#800020]/5 px-3 py-1 rounded-full">
            GET IN TOUCH
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 font-serif-editorial">
            CONTACT US
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto font-light">
            Have a question? Send us a message and we’ll get back to you shortly.
          </p>
        </div>

        {/* Contact Form Card */}
        <motion.div 
          className="bg-white border border-gray-100 rounded-lg p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* NAME FIELD */}
            <div className="space-y-2">
              <label 
                htmlFor="name" 
                className="block text-xs font-semibold tracking-widest text-[#111827] uppercase"
              >
                Name <span className="text-[#800020]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 bg-[#FAFAFA] border ${errors.name ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#800020] focus:bg-white transition-all`}
                placeholder="Your Name"
                data-testid="contact-name-input"
              />
              {errors.name && (
                <p className="text-xs text-red-500 font-light mt-1" data-testid="contact-name-error">
                  {errors.name}
                </p>
              )}
            </div>

            {/* EMAIL FIELD */}
            <div className="space-y-2">
              <label 
                htmlFor="email" 
                className="block text-xs font-semibold tracking-widest text-[#111827] uppercase"
              >
                Email <span className="text-[#800020]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 bg-[#FAFAFA] border ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#800020] focus:bg-white transition-all`}
                placeholder="your@email.com"
                data-testid="contact-email-input"
              />
              {errors.email && (
                <p className="text-xs text-red-500 font-light mt-1" data-testid="contact-email-error">
                  {errors.email}
                </p>
              )}
            </div>

            {/* PHONE FIELD (No optional label next to it) */}
            <div className="space-y-2">
              <label 
                htmlFor="phone" 
                className="block text-xs font-semibold tracking-widest text-[#111827] uppercase"
              >
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#FAFAFA] border border-gray-200 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#800020] focus:bg-white transition-all"
                placeholder="Your Phone Number"
                data-testid="contact-phone-input"
              />
            </div>

            {/* MESSAGE FIELD */}
            <div className="space-y-2">
              <label 
                htmlFor="message" 
                className="block text-xs font-semibold tracking-widest text-[#111827] uppercase"
              >
                Message <span className="text-[#800020]">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                rows={6}
                className={`w-full px-4 py-3 bg-[#FAFAFA] border ${errors.message ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#800020] focus:bg-white transition-all resize-y`}
                placeholder="How can we help you?"
                data-testid="contact-message-input"
              />
              {errors.message && (
                <p className="text-xs text-red-500 font-light mt-1" data-testid="contact-message-error">
                  {errors.message}
                </p>
              )}
            </div>

            {/* BUTTON */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#800020] hover:bg-[#660019] text-white text-xs font-semibold tracking-widest uppercase rounded-md shadow-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit-button"
              >
                {loading ? "SENDING MESSAGE..." : "SEND MESSAGE"}
              </button>
            </div>
          </form>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}

// Thank You Page Component
function ThankYouPage() {
  useEffect(() => {
    document.title = "Thank You | TDCR Consulting";
  }, []);

  return (
    <div className="bg-[#FAFAFA] text-[#111827]">
      <Header isBackToHome={true} />

      <section className="pt-8 sm:pt-12 pb-12 max-w-xl mx-auto px-4 text-center space-y-4">
        <motion.div 
          className="w-12 h-12 bg-[#800020]/5 rounded-full flex items-center justify-center text-[#800020] mx-auto mb-2"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <CheckCircle size={24} />
        </motion.div>

        <motion.h1 
          className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 font-serif-editorial"
          initial={{ y: 15, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          data-testid="thank-you-title"
        >
          THANK YOU
        </motion.h1>

        <motion.p 
          className="text-base sm:text-lg text-gray-600 leading-relaxed font-light"
          initial={{ y: 15, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          We have received your message and will reply shortly.
        </motion.p>

        <motion.div 
          className="pt-1"
          initial={{ y: 15, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <a
            href="https://tdcrconsulting.com/"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#800020] hover:bg-[#660019] text-white text-xs font-semibold tracking-widest uppercase rounded-md shadow-sm transition-colors duration-200"
            data-testid="thank-you-home-link"
          >
            RETURN TO HOME
          </a>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}

// Debt Collector Kill Switch Checkout Page Component
function DebtCollectorCheckoutPage() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [isAgreed, setIsAgreed] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  // Only mount a single live Payment Element (matching the currently visible breakpoint)
  // instead of mounting both desktop + mobile copies at once, which was doubling Stripe.js
  // initialization work and causing the ~5s delay before the card fields appeared.
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches
  );

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const handleChange = (e) => setIsMobileLayout(e.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  const desktopStripeRef = useRef(null);
  const desktopElementsRef = useRef(null);
  const mobileStripeRef = useRef(null);
  const mobileElementsRef = useRef(null);

  const handleDesktopReady = useCallback((stripe, elements) => {
    desktopStripeRef.current = stripe;
    desktopElementsRef.current = elements;
  }, []);
  const handleMobileReady = useCallback((stripe, elements) => {
    mobileStripeRef.current = stripe;
    mobileElementsRef.current = elements;
  }, []);

  useEffect(() => {
    document.title = "Checkout | Debt Collector Kill Switch";
    // Set body background to pure white
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#FFFFFF";
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  // Create the card-only PaymentIntent up front so the Payment Element only ever shows card fields.
  // A speculative fetch for this same call is already kicked off from an inline <head> script in
  // index.html the instant the checkout URL is hit (before the JS bundle even parses); reuse that
  // in-flight promise here instead of starting a second, later request.
  useEffect(() => {
    const initPromise = window.__tdcrCheckoutInit || fetch(`${BACKEND_URL}/api/checkout/init`, { method: "POST" }).then((res) => res.json());
    window.__tdcrCheckoutInit = null;
    initPromise
      .then((data) => {
        if (!data) throw new Error("init failed");
        setClientSecret(data.client_secret);
        setPaymentIntentId(data.payment_intent_id);
      })
      .catch(() => setPaymentError("Unable to load the secure payment form. Please refresh and try again."));
  }, []);

  const handleCompleteOrder = async (e, variant) => {
    e.preventDefault();
    if (isProcessing) return;

    if (!isAgreed) {
      setShowValidation(true);
      return;
    }

    const stripe = variant === "mobile" ? mobileStripeRef.current : desktopStripeRef.current;
    const elements = variant === "mobile" ? mobileElementsRef.current : desktopElementsRef.current;
    if (!stripe || !elements || !clientSecret || !paymentIntentId) return;

    setShowValidation(false);
    setPaymentError("");
    setIsProcessing(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setPaymentError(submitError.message || "Please check your payment details.");
      setIsProcessing(false);
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/checkout/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId,
          email,
          first_name: firstName,
          last_name: lastName,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Unable to start payment.");
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?order=${data.tdcr_order_id}`,
        },
        redirect: "if_required",
      });

      if (confirmError) {
        setPaymentError(confirmError.message || "Payment failed. Please try again.");
        setIsProcessing(false);
        return;
      }

      window.location.href = `/payment-success?order=${data.tdcr_order_id}`;
    } catch (err) {
      setPaymentError(err.message || "Something went wrong. Please try again.");
      setIsProcessing(false);
    }
  };

  const usStates = [
    { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }
  ];

  return (
    <div className="killswitch-page-white min-h-screen text-[#111111] font-sans antialiased selection:bg-[#ff3a30]/10 selection:text-[#ff3a30] bg-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=League+Spartan:wght@400;500;600;700;800;900&display=swap');
        
        .killswitch-page-white {
          background-color: #FFFFFF !important;
          color: #111111 !important;
          font-family: 'Inter', sans-serif !important;
        }
        
        .font-spartan {
          font-family: 'League Spartan', sans-serif !important;
        }
        
        .font-inter {
          font-family: 'Inter', sans-serif !important;
        }
        
        .killswitch-header-text {
          font-family: 'League Spartan', sans-serif !important;
          font-weight: 900 !important;
          font-size: 18px !important;
          letter-spacing: 0.1em !important;
          color: #111111 !important;
        }
        
        .killswitch-display-title-white {
          font-family: 'League Spartan', sans-serif !important;
          font-weight: 900 !important;
          font-size: 26px !important;
          letter-spacing: -0.02em !important;
          line-height: 1.15 !important;
          text-transform: uppercase !important;
        }
        
        .killswitch-section-title {
          font-family: 'League Spartan', sans-serif !important;
          font-weight: 800 !important;
          font-size: 18px !important;
          letter-spacing: -0.01em !important;
          line-height: 1.15 !important;
          text-transform: uppercase !important;
          color: #111111 !important;
        }
        
        .killswitch-btn-cta-white {
          background-color: #ff3a30 !important;
          color: #ffffff !important;
          font-family: 'League Spartan', sans-serif !important;
          font-weight: 900 !important;
          font-size: 19px !important;
          letter-spacing: 0.05em !important;
          text-transform: uppercase !important;
          border-bottom: 4px solid rgba(11, 11, 11, 0.4) !important;
          border-radius: 8px !important;
          transition: all 0.1s ease-in-out !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 100% !important;
          padding: 14px !important;
          cursor: pointer;
        }
        
        .killswitch-btn-cta-white:hover {
          background-color: #e82a20 !important;
          transform: translateY(2px) !important;
          border-bottom-width: 2px !important;
        }
        
        .killswitch-btn-cta-white:active {
          transform: translateY(4px) !important;
          border-bottom-width: 0px !important;
        }
        
        .killswitch-input-white {
          border: 2px solid #e9e3d5 !important;
          background-color: #ffffff !important;
          color: #111111 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 16px !important;
          font-weight: 500 !important;
          padding: 10px 12px !important;
          border-radius: 6px !important;
          width: 100% !important;
          transition: all 0.15s ease-in-out !important;
        }
        
        .killswitch-input-white:focus {
          outline: none !important;
          border-color: #e41c22 !important;
          box-shadow: 0 0 0 3px rgba(228, 28, 34, 0.15) !important;
        }
        
        .killswitch-select-white {
          border: 2px solid #e9e3d5 !important;
          background-color: #ffffff !important;
          color: #111111 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 16px !important;
          font-weight: 500 !important;
          padding: 10px 12px !important;
          border-radius: 6px !important;
          width: 100% !important;
          transition: all 0.15s ease-in-out !important;
          appearance: none !important;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23111111' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e") !important;
          background-repeat: no-repeat !important;
          background-position: right 14px center !important;
          background-size: 16px !important;
          padding-right: 40px !important;
        }
        
        .killswitch-select-white:focus {
          outline: none !important;
          border-color: #e41c22 !important;
          box-shadow: 0 0 0 3px rgba(228, 28, 34, 0.15) !important;
        }
        
        .killswitch-label-white {
          font-family: 'Inter', sans-serif !important;
          font-weight: 700 !important;
          color: #111111 !important;
          font-size: 14px !important;
          margin-bottom: 5px !important;
          display: block !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
        }
        
        .killswitch-divider-red-small {
          height: 3px !important;
          background-color: #e41c22 !important;
          width: 40px !important;
          margin-top: 6px !important;
          margin-bottom: 14px !important;
        }
        
        .killswitch-divider-black-thin {
          height: 1px !important;
          background-color: #111111 !important;
          width: 100% !important;
          margin-top: 10px !important;
          margin-bottom: 10px !important;
        }
        
        .killswitch-divider-light-thin {
          height: 1px !important;
          background-color: #e9e3d5 !important;
          width: 100% !important;
          margin-top: 12px !important;
          margin-bottom: 12px !important;
        }
        
        .green-box-check-small {
          background-color: #159957 !important;
          width: 20px !important;
          height: 20px !important;
          border-radius: 4px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          flex-shrink: 0 !important;
        }
      `}</style>

      {/* HEADER */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-[#e9e3d5] py-3.5 shadow-sm">
        <div className="max-w-[1140px] mx-auto px-4 flex items-center justify-between">
          <span className="killswitch-header-text uppercase font-black font-spartan">
            DEBT COLLECTOR KILL SWITCH
          </span>
          <a
            href="https://debtcollectorkillswitch.com/"
            className="font-spartan text-xs sm:text-sm font-black tracking-wider text-[#e41c22] hover:text-[#ff3a30] transition-colors uppercase"
          >
            ← GO BACK TO PREVIOUS PAGE
          </a>
        </div>
      </header>

      {/* NEW CHECKOUT HERO ADDITION — cloned from debtcollectorkillswitch.com Section 7 (gain-access) styling, per explicit content-insertion request. Existing order form below is untouched. */}
      <div className="max-w-[1120px] mx-auto px-4 md:px-6 text-center pt-8 md:pt-10 pb-4 md:pb-6">
        <p className="flex items-center justify-center gap-2 font-spartan font-black uppercase text-[#111111] text-lg sm:text-xl tracking-wide mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-6 h-6 sm:w-7 sm:h-7 text-[#e41c22] shrink-0"
            aria-hidden="true"
          >
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
            <path d="M12 9v4"></path>
            <path d="M12 17h.01"></path>
          </svg>
          <span className="bg-yellow-300 px-1.5 py-0.5 rounded">THE COLLECTION ISN&rsquo;T GOING TO REMOVE ITSELF!</span>
        </p>

        <h2 className="font-spartan font-black uppercase tracking-tight text-3xl sm:text-5xl lg:text-[56px] leading-none mb-6 max-w-4xl mx-auto">
          <span className="block text-[#111111]">THEY WANT THE FULL BALANCE.</span>
          <span className="block text-[#e41c22]">YOU DEMAND THE FULL PROOF!</span>
        </h2>

        <div className="bg-white border-2 border-[#e41c22] rounded-xl shadow-md p-4 md:p-5 max-w-3xl mx-auto mb-8">
          <p className="font-spartan font-bold text-[#111111] leading-snug text-base sm:text-lg lg:text-xl">
            SEND THE <span className="text-[#e41c22] font-black">68-DEMAND VALIDATION ATTACK.</span><br />
            FORCE THEM INTO THE <span className="underline">FEDERAL VALIDATION PROCESS</span>, IN 3 EASY STEPS.<br />
            THEN WATCH WHAT <span className="bg-yellow-300 px-1.5 py-0.5 rounded font-black">MAGICALLY HAPPENS.</span>
          </p>
        </div>

        <h3 className="font-spartan font-black uppercase tracking-tight text-[#111111] text-xl sm:text-2xl lg:text-3xl leading-snug mb-2 max-w-3xl mx-auto">
          <span className="text-[#e41c22]">DO NOT ASSUME THAT YOU OWE</span> A THIRD
        </h3>
        <h3 className="font-spartan font-black uppercase tracking-tight text-[#111111] text-2xl sm:text-3xl lg:text-4xl leading-snug mb-8">
          PARTY DEBT BUYER BUSINESS <span className="text-[#e41c22]">ANYTHING!</span>
        </h3>

        <p className="font-spartan font-bold text-[#111111]/90 leading-snug max-w-[900px] mx-auto text-xl sm:text-2xl lg:text-[28px]">
          <span className="block">DEMAND PROOF OF THEIR LEGAL AUTHORITY TO COLLECT</span>
          <span className="block mt-1">
            THEN{" "}
            <span
              style={{
                backgroundImage: "linear-gradient(120deg, rgba(255,230,0,0.8) 0%, rgba(255,230,0,0.9) 100%)",
                backgroundSize: "100% 88%",
                backgroundPosition: "0px 88%",
                backgroundRepeat: "no-repeat",
                padding: "0 4px",
              }}
            >
              WATCH HOW FAST
            </span>{" "}
            THEY FOLD!
          </span>
        </p>
      </div>

      {/* MAIN CHECKOUT BOX CONTAINER */}
      <div className="max-w-[1080px] mx-auto px-4 pt-10 pb-16">
        
        {/* ONE MAIN WHITE CHECKOUT CONTAINER */}
        <div className="bg-white border border-[#e9e3d5]/80 shadow-[0_2px_8px_rgba(17,17,17,0.04)] rounded-lg px-8 py-7 sm:py-9 md:py-10">
          
          {/* =========================================================================
              DESKTOP TWO-COLUMN LAYOUT (lg:flex, hidden on mobile)
             ========================================================================= */}
          <div className="hidden lg:grid items-start gap-x-8" style={{ gridTemplateColumns: "1fr 1px 1fr" }}>
            {/* LEFT COLUMN — 50% */}
            <div className="space-y-6 min-w-0">
              <div>
                <h2 className="killswitch-display-title-white text-black font-black uppercase leading-tight">
                  INSTANT ACCESS TO THE KILL SWITCH!
                </h2>
                <div className="killswitch-divider-red-small" />
              </div>

              <form onSubmit={(e) => handleCompleteOrder(e, "desktop")} className="space-y-5">
                {/* CONTACT INFO */}
                <div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="killswitch-label-white">First Name</label>
                        <input
                          type="text"
                          required
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="First Name"
                          className="killswitch-input-white"
                        />
                      </div>
                      <div>
                        <label className="killswitch-label-white">Last Name</label>
                        <input
                          type="text"
                          required
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Last Name"
                          className="killswitch-input-white"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="killswitch-label-white">EMAIL ADDRESS</label>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@email.com"
                        className="killswitch-input-white"
                      />
                      <p className="text-[13px] font-bold text-[#111111] mt-2 font-inter leading-relaxed">
                        Kill Switch Access Sent Over Immediately After Purchase.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="killswitch-divider-light-thin" />

                {/* PAYMENT */}
                <div>
                  <h3 className="killswitch-section-title">PAYMENT INFORMATION</h3>
                  <div className="killswitch-divider-red-small" />

                  <div data-testid="checkout-desktop-payment-element" className="pt-1" style={{ minHeight: "146px" }}>
                    {clientSecret && !isMobileLayout ? (
                      <Elements stripe={stripePromise} options={{ clientSecret, appearance: TDCR_STRIPE_APPEARANCE }}>
                        <TDCRStripeCapture onReady={handleDesktopReady} />
                        <PaymentElement options={{ layout: "accordion" }} />
                      </Elements>
                    ) : (
                      <p className="text-[13px] text-[#555] font-inter">Loading secure payment...</p>
                    )}
                  </div>
                  {paymentError && (
                    <p data-testid="checkout-desktop-payment-error" className="text-sm font-bold text-[#e41c22] mt-2 font-inter">
                      ⚠ {paymentError}
                    </p>
                  )}
                </div>

                {/* TERMS */}
                <div className="pt-2">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isAgreed}
                      onChange={(e) => {
                        setIsAgreed(e.target.checked);
                        if (e.target.checked) setShowValidation(false);
                      }}
                      className="rounded text-[#ff3a30] focus:ring-[#ff3a30] w-5 h-5 border-2 border-[#e9e3d5] flex-shrink-0"
                    />
                    <span className="text-[14px] text-[#111111] font-bold font-inter align-middle">
                      I HAVE READ AND AGREE TO THE{" "}
                      <a
                        href="https://tdcrconsulting.com/debt-collector-kill-switch/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#111111] no-underline visited:text-[#111111]"
                      >
                        PRODUCT <span className="underline">TERMS</span>
                      </a>
                    </span>
                  </label>

                  {showValidation && (
                    <p className="text-sm font-bold text-[#e41c22] mt-1 animate-pulse font-inter">
                      ⚠ Please acknowledge the Terms & Policies before completing your order.
                    </p>
                  )}
                </div>

                {/* COMPLETE ORDER BUTTON */}
                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={isProcessing}
                    data-testid="checkout-desktop-submit-btn"
                    className="killswitch-btn-cta-white font-spartan"
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>PROCESSING...</span>
                      </div>
                    ) : (
                      <span>COMPLETE ORDER</span>
                    )}
                  </button>
                </div>

                <p className="text-center text-[12px] font-bold text-[#111111] font-inter pt-1">
                  🔒 SECURE CHECKOUT ⚡ INSTANT PRIVATE ACCESS
                </p>

                {/* TRUST STRIP */}
                <div className="flex justify-center pt-1 select-none">
                  <img
                    src="/images/safe-secure-checkout.webp"
                    alt="Safe and secure checkout with SSL security, lifetime access, Stripe payment processing, and major credit cards"
                    width="900"
                    height="300"
                    decoding="async"
                    loading="eager"
                    className="w-full h-auto"
                    style={{ maxWidth: "460px", aspectRatio: "900 / 300" }}
                  />
                </div>
              </form>
            </div>

            {/* VERTICAL DIVIDER */}
            <div className="bg-[#e9e3d5] w-px h-full" />

            {/* RIGHT COLUMN — 50% */}
            <div className="w-full max-w-full min-w-0 box-border space-y-6">
              {/* CENTERED PRODUCT IMAGE — ONLY IMAGE ON PAGE */}
              <div className="flex justify-center py-2">
                <img
                  src="https://www.debtcollectorkillswitch.com/images/products/debt-collector-kill-switch-main.jpg"
                  alt="Debt Collector Kill Switch System"
                  className="h-auto w-[92%] max-w-[92%] mx-auto object-contain select-none"
                />
              </div>

              {/* PRICE RECAP */}
              <div className="space-y-2.5 text-center">
                <p className="text-[16px] font-black text-[#e41c22] uppercase whitespace-nowrap font-inter">
                  START REMOVING COLLECTIONS TODAY!
                </p>
                <p className="text-[14px] font-black text-[#111111] font-inter">
                  EVERYTHING INCLUDED VALUE: <span className="text-[#e41c22]">$2,057</span>
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="text-[#111111] font-black font-spartan text-[14px] whitespace-nowrap">
                    NORMAL PRICE:
                  </span>
                  <span className="text-[#111111] line-through decoration-[#e41c22] decoration-[2.5px] text-lg font-black font-spartan whitespace-nowrap">
                    $197
                  </span>
                  <span className="bg-[#159957] text-white text-[12px] font-black uppercase tracking-wider font-inter whitespace-nowrap px-2 py-1 rounded-full flex-shrink-0">
                    SAVE 76%
                  </span>
                </div>
                <p className="text-[14px] font-black text-[#111111] font-inter inline-block">
                  <span className="bg-yellow-300 px-1">GET INSTANT ACCESS TO THE KILL SWITCH</span>
                </p>
              </div>

              <div className="space-y-2 pt-2">
                <div className="flex items-baseline gap-2 w-full overflow-hidden">
                  <span className="text-[15px] font-bold text-[#111111] font-inter whitespace-nowrap">Debt Collector Kill Switch</span>
                  <span className="flex-1 border-b border-dotted border-[#111111]/50 mb-1"></span>
                  <span className="text-[15px] font-bold text-[#111111] font-inter whitespace-nowrap">$47.00</span>
                </div>
                <div className="flex items-baseline gap-2 w-full overflow-hidden">
                  <span className="text-[15px] font-bold text-[#111111] font-inter whitespace-nowrap">Delivery:</span>
                  <span className="flex-1 border-b border-dotted border-[#111111]/50 mb-1"></span>
                  <span className="text-[15px] font-bold text-[#111111] font-inter whitespace-nowrap">$0.00</span>
                </div>
                <div className="flex items-baseline gap-2 w-full overflow-hidden">
                  <span className="text-[15px] font-bold text-[#111111] font-inter whitespace-nowrap">Tax:</span>
                  <span className="flex-1 border-b border-dotted border-[#111111]/50 mb-1"></span>
                  <span className="text-[15px] font-bold text-[#111111] font-inter whitespace-nowrap">$0.00</span>
                </div>
                <div className="killswitch-divider-black-thin" />
                <div className="flex items-baseline justify-between gap-3 text-[#111111] font-spartan">
                  <span className="text-lg font-black uppercase tracking-wider">TOTAL:</span>
                  <span className="text-2xl font-black flex-shrink-0">$47</span>
                </div>
              </div>

              <div className="killswitch-divider-light-thin" />

              {/* 4 BENEFIT ROWS */}
              <div className="space-y-3.5">
                <div className="flex items-center gap-3">
                  <div className="green-box-check-small">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <span className="text-[15px] font-extrabold text-[#111111] tracking-wide uppercase leading-snug font-inter min-w-0">
                    25+ CUSTOM ATTACK FILES
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="green-box-check-small">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <span className="text-[15px] font-extrabold text-[#111111] tracking-wide uppercase leading-snug font-inter min-w-0">
                    68 VALIDATION DEMANDS
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="green-box-check-small">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <span className="text-[15px] font-extrabold text-[#111111] tracking-wide uppercase leading-snug font-inter min-w-0">
                    CHOOSE YOUR SITUATION & FILL IN THE BLANKS
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="green-box-check-small">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <span className="text-[15px] font-extrabold text-[#111111] tracking-wide uppercase leading-snug font-inter min-w-0">
                    3 FREE BONUSES & MUCH MORE..
                  </span>
                </div>
              </div>

              <div className="killswitch-divider-light-thin" />

              {/* TESTIMONIALS — CUSTOMER REVIEWS IMAGE */}
              <div className="flex justify-center">
                <img
                  src="/images/checkout-testimonials.webp"
                  alt="Real customer reviews for Debt Collector Kill Switch"
                  width="920"
                  height="690"
                  decoding="async"
                  loading="lazy"
                  className="w-full h-auto max-w-full"
                  style={{ aspectRatio: "920 / 690" }}
                />
              </div>

            </div>

          </div>

          {/* =========================================================================
              MOBILE SINGLE-COLUMN LAYOUT (lg:hidden, hidden on desktop)
             ========================================================================= */}
          <div className="lg:hidden block space-y-8">

            {/* 1. PRODUCT IMAGE — ONLY IMAGE ON PAGE (shown first on mobile so the customer sees what they're buying before entering payment details) */}
            <div className="flex justify-center py-2">
              <img
                src="https://www.debtcollectorkillswitch.com/images/products/debt-collector-kill-switch-main.jpg"
                alt="Debt Collector Kill Switch System"
                className="h-auto w-[92%] max-w-[92%] mx-auto object-contain select-none"
              />
            </div>

            {/* 2. PRICE RECAP */}
            <div className="space-y-2.5 text-center">
              <p className="text-[15px] font-black text-[#e41c22] uppercase whitespace-nowrap font-inter">
                START REMOVING COLLECTIONS TODAY!
              </p>
              <p className="text-[13px] font-black text-[#111111] font-inter">
                EVERYTHING INCLUDED VALUE: <span className="text-[#e41c22]">$2,057</span>
              </p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span className="text-[#111111] font-black font-spartan text-[13px] whitespace-nowrap">
                  NORMAL PRICE:
                </span>
                <span className="text-[#111111] line-through decoration-[#e41c22] decoration-[2px] text-base font-black font-spartan whitespace-nowrap">
                  $197
                </span>
                <span className="bg-[#159957] text-white text-[11px] font-black uppercase tracking-wider font-inter whitespace-nowrap px-2 py-1 rounded-full flex-shrink-0">
                  SAVE 76%
                </span>
              </div>
              <p className="text-[13px] font-black text-[#111111] font-inter inline-block">
                <span className="bg-yellow-300 px-1">GET INSTANT ACCESS TO THE KILL SWITCH</span>
              </p>
            </div>

            <div className="space-y-2 pt-2">
              <div className="flex items-baseline gap-2 w-full overflow-hidden">
                <span className="text-[14px] font-bold text-[#111111] font-inter whitespace-nowrap">Debt Collector Kill Switch</span>
                <span className="flex-1 border-b border-dotted border-[#111111]/50 mb-1"></span>
                <span className="text-[14px] font-bold text-[#111111] font-inter whitespace-nowrap">$47.00</span>
              </div>
              <div className="flex items-baseline gap-2 w-full overflow-hidden">
                <span className="text-[14px] font-bold text-[#111111] font-inter whitespace-nowrap">Delivery:</span>
                <span className="flex-1 border-b border-dotted border-[#111111]/50 mb-1"></span>
                <span className="text-[14px] font-bold text-[#111111] font-inter whitespace-nowrap">$0.00</span>
              </div>
              <div className="flex items-baseline gap-2 w-full overflow-hidden">
                <span className="text-[14px] font-bold text-[#111111] font-inter whitespace-nowrap">Tax:</span>
                <span className="flex-1 border-b border-dotted border-[#111111]/50 mb-1"></span>
                <span className="text-[14px] font-bold text-[#111111] font-inter whitespace-nowrap">$0.00</span>
              </div>
              <div className="killswitch-divider-black-thin" />
              <div className="flex items-baseline justify-between gap-3 text-[#111111] font-spartan">
                <span className="text-lg font-black uppercase tracking-wider">TOTAL:</span>
                <span className="text-2xl font-black flex-shrink-0">$47</span>
              </div>
            </div>

            <div className="killswitch-divider-light-thin" />

            {/* 3. 4 BENEFITS */}
            <div className="space-y-3.5">
              <div className="flex items-center gap-3">
                <div className="green-box-check-small">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <span className="text-[14px] font-extrabold text-[#111111] tracking-wide uppercase leading-snug font-inter min-w-0">
                  25+ CUSTOM ATTACK FILES
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="green-box-check-small">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <span className="text-[14px] font-extrabold text-[#111111] tracking-wide uppercase leading-snug font-inter min-w-0">
                  68 VALIDATION DEMANDS
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="green-box-check-small">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <span className="text-[14px] font-extrabold text-[#111111] tracking-wide uppercase leading-snug font-inter min-w-0">
                  CHOOSE YOUR SITUATION & FILL IN THE BLANKS
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="green-box-check-small">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <span className="text-[14px] font-extrabold text-[#111111] tracking-wide uppercase leading-snug font-inter min-w-0">
                  3 FREE BONUSES & MUCH MORE..
                </span>
              </div>
            </div>

            <div className="killswitch-divider-light-thin" />

            {/* 4. TESTIMONIALS — CUSTOMER REVIEWS IMAGE */}
            <div className="flex justify-center">
              <img
                src="/images/checkout-testimonials.webp"
                alt="Real customer reviews for Debt Collector Kill Switch"
                width="920"
                height="690"
                decoding="async"
                loading="lazy"
                className="w-full h-auto max-w-full"
                style={{ aspectRatio: "920 / 690" }}
              />
            </div>

            <div className="killswitch-divider-light-thin" />

            {/* 5. COMPLETE YOUR ORDER HEADER */}
            <div>
              <h2 className="killswitch-display-title-white text-2xl font-black text-[#111111]">
                INSTANT ACCESS TO THE KILL SWITCH!
              </h2>
              <div className="killswitch-divider-red-small" />
            </div>

            <form onSubmit={(e) => handleCompleteOrder(e, "mobile")} className="space-y-5">
              {/* 6-7. CONTACT INFO */}
              <div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="killswitch-label-white">First Name</label>
                      <input
                        type="text"
                        required
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First Name"
                        className="killswitch-input-white"
                      />
                    </div>
                    <div>
                      <label className="killswitch-label-white">Last Name</label>
                      <input
                        type="text"
                        required
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last Name"
                        className="killswitch-input-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="killswitch-label-white">EMAIL ADDRESS</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@email.com"
                      className="killswitch-input-white"
                    />
                    <p className="text-[12px] font-bold text-[#111111] mt-2 font-inter leading-relaxed">
                      Kill Switch Access Sent Over Immediately After Purchase.
                    </p>
                  </div>
                </div>
              </div>

              <div className="killswitch-divider-light-thin" />

              {/* 8. PAYMENT */}
              <div>
                <h3 className="killswitch-section-title">PAYMENT INFORMATION</h3>
                <div className="killswitch-divider-red-small" />

                <div data-testid="checkout-mobile-payment-element" className="pt-1" style={{ minHeight: "146px" }}>
                  {clientSecret && isMobileLayout ? (
                    <Elements stripe={stripePromise} options={{ clientSecret, appearance: TDCR_STRIPE_APPEARANCE }}>
                      <TDCRStripeCapture onReady={handleMobileReady} />
                      <PaymentElement options={{ layout: "accordion" }} />
                    </Elements>
                  ) : (
                    <p className="text-[13px] text-[#555] font-inter">Loading secure payment...</p>
                  )}
                </div>
                {paymentError && (
                  <p data-testid="checkout-mobile-payment-error" className="text-sm font-bold text-[#e41c22] mt-2 font-inter">
                    ⚠ {paymentError}
                  </p>
                )}
              </div>

              {/* 9. TERMS */}
              <div className="pt-1">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isAgreed}
                    onChange={(e) => {
                      setIsAgreed(e.target.checked);
                      if (e.target.checked) setShowValidation(false);
                    }}
                    className="rounded text-[#ff3a30] focus:ring-[#ff3a30] w-5 h-5 border-2 border-[#e9e3d5] flex-shrink-0"
                  />
                  <span className="text-[14px] text-[#111111] font-bold font-inter align-middle">
                    I HAVE READ AND AGREE TO THE{" "}
                    <a
                      href="https://tdcrconsulting.com/debt-collector-kill-switch/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#111111] no-underline visited:text-[#111111]"
                    >
                      PRODUCT <span className="underline">TERMS</span>
                    </a>
                  </span>
                </label>

                {showValidation && (
                  <p className="text-sm font-bold text-[#e41c22] mt-1 animate-pulse font-inter">
                    ⚠ Please acknowledge the Terms & Policies before completing your order.
                  </p>
                )}
              </div>

              {/* 10. COMPLETE ORDER BUTTON */}
              <div className="pt-1">
                <button
                  type="submit"
                  disabled={isProcessing}
                  data-testid="checkout-mobile-submit-btn"
                  className="killswitch-btn-cta-white font-spartan"
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>PROCESSING...</span>
                    </div>
                  ) : (
                    <span>COMPLETE ORDER</span>
                  )}
                </button>
              </div>

              <p className="text-center text-[12px] font-bold text-[#111111] font-inter pt-1">
                🔒 SECURE CHECKOUT ⚡ INSTANT PRIVATE ACCESS
              </p>

              {/* 11. TRUST STRIP */}
              <div className="flex justify-center pt-1 select-none">
                <img
                  src="/images/safe-secure-checkout.webp"
                  alt="Safe and secure checkout with SSL security, lifetime access, Stripe payment processing, and major credit cards"
                  width="900"
                  height="300"
                  decoding="async"
                  loading="eager"
                  className="w-full h-auto"
                  style={{ maxWidth: "100%", aspectRatio: "900 / 300" }}
                />
              </div>
            </form>

          </div>

          {/* Final line — cloned from DCKS's own bottom headline style, remains inside the existing order-form container */}
          <div className="mt-8 md:mt-10 text-center">
            <h3 className="font-spartan font-black uppercase tracking-tight text-[#e41c22] text-xl sm:text-2xl lg:text-3xl leading-snug">
              THE ULTIMATE DEBT COLLECTOR ATTACK SYSTEM!
            </h3>
          </div>

        </div>

      </div>

      {/* 14. FOOTER (rendered completely outside and below the box) */}
      <footer className="py-8 bg-transparent border-t border-[#e9e3d5]/60 text-center">
        <p className="text-[13px] font-bold text-[#111111] tracking-wide font-inter">
          © 2026 Debt Collector Kill Switch. All Rights Reserved.
        </p>
      </footer>
    </div>
  );
}


// TDCR Consulting — Temporary Payment Success Landing (server-side status confirmed via /api/checkout/status)
// Once Stripe payment is server-verified as paid, redirects to the DCKS thank-you page with the secure access token.
function TDCRPaymentSuccessPage() {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    document.title = "Payment Status | TDCR Consulting";
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("order");
    if (!orderId) {
      setStatus("unknown");
      return;
    }

    let attempts = 0;
    let intervalId;
    let cancelled = false;

    const poll = () => {
      attempts += 1;
      fetch(`${BACKEND_URL}/api/checkout/status/${orderId}`)
        .then((res) => res.json())
        .then((data) => {
          if (cancelled) return;
          if (data.payment_status === "paid" && data.redirect_url) {
            clearInterval(intervalId);
            setStatus("paid");
            window.location.href = data.redirect_url;
          } else if (data.payment_status === "failed") {
            clearInterval(intervalId);
            setStatus("failed");
          } else if (attempts >= 8) {
            clearInterval(intervalId);
            setStatus("unknown");
          } else {
            setStatus("pending");
          }
        })
        .catch(() => {
          if (!cancelled && attempts >= 8) setStatus("unknown");
        });
    };

    poll();
    intervalId = setInterval(poll, 1500);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  return (
    <div data-testid="tdcr-payment-success-page" className="min-h-screen flex items-center justify-center bg-[#FFFDF5] text-[#111111] font-sans px-6">
      <div className="text-center max-w-md space-y-3">
        <h1 data-testid="payment-status-heading" className="text-2xl font-bold">
          {status === "paid" && "Payment Received"}
          {(status === "pending" || status === "checking") && "Confirming Your Payment..."}
          {status === "failed" && "Payment Not Completed"}
          {status === "unknown" && "Payment Status Unavailable"}
        </h1>
        <p data-testid="payment-status-message" className="text-sm text-[#555]">
          {status === "paid" && "Thank you — your TDCR Consulting payment was processed successfully. Redirecting you to your access page..."}
          {(status === "pending" || status === "checking") && "Please wait while we confirm your payment status."}
          {status === "failed" && "Your payment was not completed. Please contact info@tdcrconsulting.com."}
          {status === "unknown" && "We could not verify this order. Please contact info@tdcrconsulting.com."}
        </p>
      </div>
    </div>
  );
}

// Debt Collector Kill Switch — Product Purchase Terms Page
function DebtCollectorTermsPage() {
  useEffect(() => {
    document.title = "Product Purchase Terms | Debt Collector Kill Switch";
  }, []);

  const handleCloseWindow = () => {
    window.close();
    setTimeout(() => {
      window.location.href = "https://tdcrconsulting.com/debt-collector-kill-switch/checkout";
    }, 300);
  };

  const SectionTitle = ({ number, children }) => (
    <div className="mb-2">
      <h3 className="font-inter font-bold text-[#111111]" style={{ fontSize: "18px" }}>
        <span className="font-bold">{number}.</span> {children}
      </h3>
      <div className="killswitch-terms-divider-red" />
    </div>
  );

  return (
    <div className="killswitch-terms-page min-h-screen bg-white text-[#111111] font-inter">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=League+Spartan:wght@400;700;800;900&display=swap');

        .killswitch-terms-page {
          font-family: 'Inter', sans-serif !important;
          background-color: #ffffff !important;
        }

        .killswitch-terms-page h1,
        .killswitch-terms-page h2,
        .killswitch-terms-page h3 {
          font-family: 'League Spartan', sans-serif !important;
        }

        .killswitch-terms-page p,
        .killswitch-terms-page li,
        .killswitch-terms-page a,
        .killswitch-terms-page button {
          font-family: 'Inter', sans-serif !important;
        }

        .font-spartan {
          font-family: 'League Spartan', sans-serif !important;
        }

        .killswitch-terms-divider-red {
          height: 2px;
          background-color: #e41c22;
          width: 32px;
          margin-top: 6px;
          margin-bottom: 10px;
        }

        .killswitch-terms-body p {
          margin-bottom: 12px;
          line-height: 1.6;
        }

        .killswitch-terms-body ul {
          margin: 0 0 12px 0;
          padding-left: 20px;
        }

        .killswitch-terms-body li {
          margin-bottom: 6px;
          line-height: 1.6;
        }

        .killswitch-terms-email {
          color: #e41c22;
          font-weight: 700;
          text-decoration: underline;
        }
      `}</style>

      <div className="max-w-[820px] mx-auto px-4 py-10 sm:py-14">
        <div
          className="bg-white border border-[#e9e3d5] shadow-[0_2px_10px_rgba(17,17,17,0.05)] rounded-lg p-6 sm:p-10"
          data-testid="killswitch-terms-box"
        >
          {/* TITLE BLOCK */}
          <div className="mb-4">
            <h1
              className="font-spartan font-black uppercase text-black tracking-tight"
              style={{ fontSize: "24px" }}
              data-testid="killswitch-terms-page-title"
            >
              DEBT COLLECTOR KILL SWITCH
            </h1>
            <h2
              className="font-spartan font-black uppercase text-[#e41c22] mt-1"
              style={{ fontSize: "18px" }}
              data-testid="killswitch-terms-heading"
            >
              PRODUCT PURCHASE TERMS
            </h2>
            <p className="text-sm text-[#111111] font-medium mt-2" data-testid="killswitch-terms-effective-date">
              Effective Date: July 1, 2026
            </p>
            <div className="h-[2px] bg-[#e41c22] w-full mt-4" />
          </div>

          {/* BODY */}
          <div className="killswitch-terms-body text-[15px] text-[#111111]" data-testid="killswitch-terms-body">
            <p>
              Thanks for choosing the Debt Collector Kill Switch. We want your purchase to be simple and your expectations clear. These Product Purchase Terms apply specifically to your purchase of the Debt Collector Kill Switch from TDCR Consulting, LLC.
            </p>

            <SectionTitle number={1}>WHAT YOU’RE PURCHASING</SectionTitle>
            <p>
              You are purchasing the Debt Collector Kill Switch, a self-help digital educational and document-drafting product designed for U.S. consumers dealing with debt collection and credit-reporting situations.
            </p>
            <p>
              Your purchase includes the digital files, guides, templates, instructions, bonuses, and other materials identified in the offer available at the time you purchase.
            </p>
            <p>
              This is a digital product, not legal representation, and TDCR Consulting, LLC does not act on your behalf with debt collectors, credit bureaus, courts, creditors, or government agencies.
            </p>

            <SectionTitle number={2}>YOUR PURCHASE PRICE</SectionTitle>
            <p>
              The purchase price shown at checkout is the total one-time price for the product selected.
            </p>
            <p>For the current Debt Collector Kill Switch offer:</p>
            <p><strong>Debt Collector Kill Switch — $47.00</strong></p>
            <p>
              There are no recurring subscription charges unless you separately and affirmatively purchase another product or service that clearly states otherwise.
            </p>

            <SectionTitle number={3}>WHAT YOU’LL SEE ON YOUR CARD</SectionTitle>
            <p>Your payment is processed for the product by TDCR Consulting, LLC.</p>
            <p>Your credit or debit card statement will show:</p>
            <p><strong>TDCR CONSULTING LLC</strong></p>
            <p>
              If you ever see this charge and have a question about your purchase, please contact us before assuming you do not recognize it:
            </p>
            <p>
              <a href="mailto:info@debtcollectorkillswitch.com" className="killswitch-terms-email" data-testid="killswitch-terms-email-link-1">
                info@debtcollectorkillswitch.com
              </a>
            </p>

            <SectionTitle number={4}>WHAT HAPPENS AFTER YOU PAY</SectionTitle>
            <p>
              After your payment is successfully completed, you should receive access to your Debt Collector Kill Switch digital materials.
            </p>
            <p>
              Your secure access information will also be sent to the email address you provide during checkout.
            </p>
            <p><strong>Please double-check your email address before submitting your order.</strong></p>
            <p>
              If you experience a legitimate delivery or access problem, contact us and we will work with you to restore or re-deliver your purchased materials.
            </p>

            <SectionTitle number={5}>DIGITAL DELIVERY & ACCESS</SectionTitle>
            <p>Because this is a digital product, delivery can begin immediately after payment.</p>
            <p>Your access is intended for your personal use only.</p>
            <p>
              Please do not share, resell, upload, publish, distribute, reproduce for others, or provide your private download/access credentials to anyone else.
            </p>
            <p>
              Your purchase includes access to the edition and materials included with your offer, subject to normal technical availability and these Purchase Terms.
            </p>

            <SectionTitle number={6}>DIGITAL SALES ARE FINAL</SectionTitle>
            <p>
              Because the materials can be accessed, downloaded, saved, copied, printed, or used immediately after purchase, <strong>digital-product sales are final and non-refundable except where required by applicable law.</strong>
            </p>
            <p>That said, we are not looking to leave you hanging.</p>
            <p>
              If you genuinely have an access problem, cannot locate something you purchased, or are unsure where to begin inside the Kill Switch, contact:
            </p>
            <p>
              <a href="mailto:info@debtcollectorkillswitch.com" className="killswitch-terms-email" data-testid="killswitch-terms-email-link-2">
                info@debtcollectorkillswitch.com
              </a>
            </p>
            <p>We will make a reasonable effort to help you access and navigate what you purchased.</p>

            <SectionTitle number={7}>WHAT WE CAN — AND CANNOT — PROMISE</SectionTitle>
            <p>
              The Kill Switch gives you educational information, document-drafting tools, templates, instructions, and a structured process to use yourself.
            </p>
            <p><strong>We cannot guarantee a specific result.</strong></p>
            <p>
              Debt collectors, credit bureaus, creditors, courts, furnishers, and government agencies are independent third parties, and their responses are outside our control.
            </p>
            <p>We do not guarantee:</p>
            <ul>
              <li>deletion of a collection;</li>
              <li>a particular credit-score change;</li>
              <li>that collection activity will stop;</li>
              <li>a specific response or response time;</li>
              <li>dismissal of a lawsuit;</li>
              <li>reversal of a judgment or garnishment; or</li>
              <li>any particular financial or legal outcome.</li>
            </ul>
            <p>
              Examples, testimonials, and timeframes shown on the website describe individual experiences and are not promises that every customer will receive the same result.
            </p>

            <SectionTitle number={8}>USE THE MATERIALS TRUTHFULLY</SectionTitle>
            <p>
              The Kill Switch is designed to help consumers exercise legitimate rights and organize legitimate disputes or requests.
            </p>
            <p>Only provide information that is truthful and accurate to the best of your knowledge.</p>
            <p>
              Do not use the materials to submit false statements, fabricated evidence, false identity-theft claims, or knowingly inaccurate information.
            </p>

            <SectionTitle number={9}>U.S. CUSTOMERS ONLY</SectionTitle>
            <p>The Debt Collector Kill Switch is currently offered only to:</p>
            <p><strong>U.S. residents located in the United States who are 18 years of age or older.</strong></p>
            <p>
              The materials are designed around U.S. consumer issues and are not intended for use in other countries.
            </p>

            <SectionTitle number={10}>LEGAL MATTERS & DEADLINES</SectionTitle>
            <p>
              The materials are educational and self-help resources. <strong>TDCR Consulting, LLC is not a law firm and does not provide legal representation.</strong>
            </p>
            <p>
              If you have been served with a lawsuit, have a court hearing, judgment, garnishment, levy, appeal deadline, or another time-sensitive legal matter, consider speaking promptly with a licensed attorney in your jurisdiction.
            </p>

            <SectionTitle number={11}>ELECTRONIC PURCHASE AGREEMENT</SectionTitle>
            <p>
              By checking the purchase-agreement box and clicking <strong>COMPLETE ORDER</strong>, you are confirming that you have reviewed and accepted these Product Purchase Terms and the applicable Refund Policy.
            </p>

            <SectionTitle number={12}>QUESTIONS ABOUT YOUR PURCHASE</SectionTitle>
            <p>Questions about your Debt Collector Kill Switch purchase may be sent to:</p>
            <p>
              <a href="mailto:info@debtcollectorkillswitch.com" className="killswitch-terms-email" data-testid="killswitch-terms-email-link-3">
                info@debtcollectorkillswitch.com
              </a>
            </p>
            <p className="font-bold">Debt Collector Kill Switch</p>
            <p>Fulfilled by TDCR Consulting, LLC</p>
          </div>

          {/* CLOSE / RETURN */}
          <div className="flex justify-center pt-4 border-t border-[#e9e3d5] mt-6">
            <button
              onClick={handleCloseWindow}
              className="font-spartan font-black uppercase text-sm tracking-widest text-[#e41c22] hover:text-[#ff3a30] transition-colors"
              data-testid="killswitch-terms-close-button"
            >
              CLOSE WINDOW
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main App Router Component
function App() {
  return (
    <div className="App selection:bg-[#800020]/10 selection:text-[#800020]">
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/sms-terms" element={<SmsTermsPage />} />
          <Route path="/debt-collector-kill-switch/checkout" element={<DebtCollectorCheckoutPage />} />
          <Route path="/debt-collector-kill-switch/terms" element={<DebtCollectorTermsPage />} />
          <Route path="/payment-success" element={<TDCRPaymentSuccessPage />} />
          {/* Fallback to main route */}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
